import React, { useState, useEffect } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { Play, Pause, Heart, Share2, Coins, Download, Video, Flag } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { coverFor, fmtTime, Waveform } from "@/components/common";
import { TipModal } from "@/components/TipModal";

export default function SongPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { current, playing, playSong, toggle } = usePlayer();
  const { openDownload, openLyricVideo } = useOutletContext();
  const [song, setSong] = useState(null);
  const [liked, setLiked] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);

  const load = () => api.get(`/songs/${id}`).then((r) => { setSong(r.data.song); setLiked(r.data.song.liked); }).catch(() => setSong(false));
  useEffect(() => { load(); }, [id]);

  if (song === null) return <div className="py-24 text-center text-slate-500">Loading…</div>;
  if (song === false) return <div className="py-24 text-center text-slate-500">Song not found or private</div>;

  const isCurrent = current?.id === song.id;
  const like = async () => {
    if (!user) return toast.error("Sign in to like");
    try { if (liked) { await api.delete(`/songs/${song.id}/like`); setLiked(false); } else { await api.post(`/songs/${song.id}/like`); setLiked(true); } load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: song.title, url }); } catch {} }
    else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };
  const report = async () => {
    const reason = window.prompt("Report reason (copyright, impersonation, abuse)");
    if (reason) { await api.post("/reports", { target_type: "song", target_id: song.id, reason }); toast.success("Report submitted"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 max-w-3xl mx-auto">
      <div className="jx-radial-bloom border border-white/10 rounded-3xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-6">
          <img src={coverFor(song)} className="w-full sm:w-52 h-52 rounded-2xl object-cover jx-glow-purple" alt="" />
          <div className="flex-1 flex flex-col">
            <p className="text-xs font-mono uppercase tracking-wider text-purple-400">{song.genre} {song.source === "ai" && "• AI generated"}</p>
            <h1 className="text-3xl font-black font-display mt-1">{song.title}</h1>
            <Link to={`/u/${song.creator_username}`} className="text-slate-400 mt-1 hover:text-white">{song.creator_name}</Link>
            <div className="flex gap-4 text-xs text-slate-500 mt-2">
              <span>{song.plays} plays</span><span>{song.likes} likes</span><span>{fmtTime(song.duration)}</span>
            </div>
            {isCurrent && playing && <Waveform bars={10} className="h-6 mt-3" />}
            <div className="flex flex-wrap items-center gap-2 mt-auto pt-4">
              <button onClick={() => isCurrent ? toggle() : playSong(song, [song])} data-testid="song-play-btn" className="jx-cta text-white font-semibold px-6 py-2.5 rounded-xl flex items-center gap-2">
                {isCurrent && playing ? <Pause size={17} /> : <Play size={17} />} {isCurrent && playing ? "Pause" : "Play"}
              </button>
              <button onClick={like} data-testid="song-like-btn" className={`p-2.5 rounded-xl border border-white/15 ${liked ? "text-rose-400" : "text-slate-300"}`}><Heart size={17} className={liked ? "fill-rose-400" : ""} /></button>
              <button onClick={share} className="p-2.5 rounded-xl border border-white/15 text-slate-300"><Share2 size={17} /></button>
              <button onClick={() => openDownload(song)} className="p-2.5 rounded-xl border border-white/15 text-slate-300"><Download size={17} /></button>
              <button onClick={() => openLyricVideo(song)} className="p-2.5 rounded-xl border border-white/15 text-slate-300"><Video size={17} /></button>
              {user && user.id !== song.creator_id && <button onClick={() => setTipOpen(true)} data-testid="song-tip-btn" className="p-2.5 rounded-xl border border-white/15 text-slate-300"><Coins size={17} /></button>}
              <button onClick={report} className="p-2.5 rounded-xl border border-white/15 text-slate-500"><Flag size={16} /></button>
            </div>
          </div>
        </div>
      </div>

      {song.lyrics && (
        <div className="jx-card rounded-2xl p-5">
          <h2 className="font-semibold mb-2">Lyrics</h2>
          <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{song.lyrics}</pre>
        </div>
      )}

      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={{ id: song.creator_id, display_name: song.creator_name }} songId={song.id} onDone={load} />
    </div>
  );
}
